import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Box, Drawer, List, ListItemButton, ListItemText, Toolbar, Typography } from "@mui/material";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../supabaseClient";

const LEAGUE_PATHS = ["/schedule", "/standings", "/stats"];

/** Youth and MBL only get a menu entry once they have fixtures. */
function useExtraLeagues() {
  const [extra, setExtra] = useState({ youth: false, mbl: false });
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      supabase.from("youth_fixtures").select("id", { count: "exact", head: true }),
      supabase.from("fixtures").select("id", { count: "exact", head: true }).eq("sport", "MBL Soccer"),
    ]).then(([y, m]) => {
      if (!cancelled) setExtra({ youth: (y.count || 0) > 0, mbl: (m.count || 0) > 0 });
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return extra;
}

const isActive = (item, pathname) => {
  if (item.match) return item.match(pathname);
  return pathname === item.path;
};

const Section = ({ label, children }) => (
  <Box sx={{ mt: 1 }}>
    {label && (
      <Typography sx={{ px: 2.5, pt: 1.5, pb: 0.5, fontSize: "0.68rem", fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(0,0,0,0.4)" }}>
        {label}
      </Typography>
    )}
    <List disablePadding>{children}</List>
  </Box>
);

const NavDrawer = ({ open, onClose, width }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const extra = useExtraLeagues();

  const league = [
    { text: "Home", hint: user ? "Your team and next game" : null, path: "/" },
    {
      text: "Fixtures & tables",
      hint: "Results, standings, stats",
      path: "/schedule",
      match: (p) => LEAGUE_PATHS.includes(p) || p.startsWith("/team/"),
    },
    { text: "Insights", path: "/insights" },
    { text: "Match photos", hint: "View, download and share", path: "/photos" },
    { text: "Rules", path: "/rules" },
    { text: "Jobs", hint: "Work with HADAF", path: "/jobs" },
    extra.youth && { text: "Youth league", path: "/youth" },
    extra.mbl && { text: "MBL", path: "/mbl" },
  ].filter(Boolean);

  const join = [
    { text: "Register", path: "/register/select-division", match: (p) => p.startsWith("/register") && p !== "/register" },
  ];

  const account = user
    ? [
        { text: "Profile", path: "/profile" },
        {
          text: "Sign out",
          onClick: async () => {
            await signOut();
            navigate("/", { replace: true });
          },
        },
      ]
    : [{ text: "Sign in", path: "/register?auth=1", match: (p) => p === "/register" }];

  const renderItem = (item) => {
    const active = item.path ? isActive(item, pathname) : false;
    return (
      <ListItemButton
        key={item.text}
        {...(item.path ? { component: Link, to: item.path } : {})}
        onClick={() => {
          onClose();
          item.onClick?.();
        }}
        selected={active}
        sx={{
          mx: 1,
          borderRadius: 2,
          py: 0.9,
          "&.Mui-selected": { bgcolor: "#f1f1f1" },
          "&.Mui-selected:hover": { bgcolor: "#ebebeb" },
        }}
      >
        <ListItemText
          primary={item.text}
          secondary={item.hint}
          primaryTypographyProps={{ fontWeight: active ? 800 : 600, color: "#111", fontSize: "0.95rem" }}
          secondaryTypographyProps={{ fontSize: "0.75rem" }}
        />
      </ListItemButton>
    );
  };

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      sx={{ "& .MuiDrawer-paper": { width, boxSizing: "border-box", backgroundColor: "#fff", borderRight: "1px solid #eee" } }}
    >
      <Toolbar />
      <Section>{league.map(renderItem)}</Section>
      <Section>{join.map(renderItem)}</Section>
      <Section label={user ? "Account" : null}>{account.map(renderItem)}</Section>
    </Drawer>
  );
};

export default NavDrawer;
